// app/src/simulations/runContributeV2Send.ts
import { Buffer } from "buffer";
import { Connection, PublicKey } from "@solana/web3.js";
import { sendContributeV2 } from "./sendContributeV2";
import { fetchTxDetails, waitForSignature } from "./txVerify";
import { saveReceipt } from "./receiptStore";
import { envProbe } from "../envProbe";

/**
 * Runtime config resolution (single source of truth).
 * Priority: window runtime overrides > Vite envProbe > defaults
 *
 * Best practice: keep imports stable (NO dynamic imports / cache-busting) to avoid Vite import-analysis failures.
 */
function resolveRuntime() {
  const w = typeof window !== "undefined" ? (window as any) : {};
  const rt =
    w.__WEARE_SUSU_RUNTIME_CONFIG__ ||
    w.__WEARE_SUSU_RUNTIME__ ||
    w.__WEARE_SUSU_CONFIG__ ||
    {};

  // envProbe is a pure snapshot (side-effect-free)
  const env = envProbe || {};

  const programId =
    (rt.programId && String(rt.programId).trim()) ||
    (env.VITE_PROGRAM_ID && String(env.VITE_PROGRAM_ID).trim()) ||
    // allow legacy fallback if present
    ((env as any).VITE_WEARE_SUSU_PROGRAM_ID && String((env as any).VITE_WEARE_SUSU_PROGRAM_ID).trim()) ||
    "";

  const endpoint =
    (rt.endpoint && String(rt.endpoint).trim()) ||
    (env.VITE_SOLANA_RPC && String(env.VITE_SOLANA_RPC).trim()) ||
    ((env as any).VITE_SOLANA_ENDPOINT && String((env as any).VITE_SOLANA_ENDPOINT).trim()) ||
    "https://api.testnet.solana.com";

  const mode = String(env.MODE || rt.APP_ENV || env.VITE_SOLANA_CLUSTER || "unknown");

  return { programId, endpoint, mode };
}

function asTrimmed(v: unknown) {
  return String(v ?? "").trim();
}

function deriveMemberV2Pda(programId: PublicKey, poolV2: PublicKey, wallet: PublicKey): PublicKey {
  // IDL seeds show bytes for "member_v2"
  return PublicKey.findProgramAddressSync(
    [Buffer.from("member_v2"), poolV2.toBuffer(), wallet.toBuffer()],
    programId
  )[0];
}

function deriveVaultV2Pda(programId: PublicKey, poolV2: PublicKey): PublicKey {
  // IDL seeds show bytes for "vault_v2"
  return PublicKey.findProgramAddressSync([Buffer.from("vault_v2"), poolV2.toBuffer()], programId)[0];
}

async function preflightPoolExists(connection: Connection, poolV2: PublicKey) {
  const info = await connection.getAccountInfo(poolV2, "confirmed");
  if (!info) {
    throw new Error(
      `Preflight failed: pool_v2 account not found on this cluster.\n- pool_v2: ${poolV2.toBase58()}`
    );
  }
  return info;
}

/**
 * SEND runner for contribute_v2.
 *
 * Policy:
 * - Derive PDAs by default (member_v2, vault_v2).
 * - If overrides are supplied, validate them and hard-fail on mismatch.
 * - Preflight: ensure pool exists before asking wallet to sign.
 * - Receipt: always recorded (success or fail).
 */
export async function runContributeV2Send(input: {
  pool_v2: string;
  lamports: string;

  /** Optional overrides (Advanced mode). Must match derived PDAs. */
  member_v2?: string;
  vault_v2?: string;

  commitment?: "processed" | "confirmed" | "finalized";
  wallet?: any;

  verifyTimeoutMs?: number;

  /** Optional fee headroom passthrough to sendContributeV2 (if your sendContributeV2 supports it) */
  feeBufferLamports?: number;
}) {
  const rt = resolveRuntime();

  if (!rt.programId) {
    throw new Error(
      `ProgramId is not configured.\n` +
        `- mode: ${rt.mode}\n` +
        `- endpoint: ${rt.endpoint}\n` +
        `Expected VITE_PROGRAM_ID (envProbe) or window.__WEARE_SUSU_RUNTIME_CONFIG__.programId`
    );
  }

  const commitment = input.commitment ?? "confirmed";
  const connection = new Connection(rt.endpoint, commitment);

  // Receipt context
  const createdAt = new Date().toISOString();
  const baseReceipt = {
    kind: "contribute_v2" as const,
    signature: "",
    createdAt,
    endpoint: rt.endpoint,
    programId: rt.programId,
    pool_v2: input.pool_v2,
    member_v2: input.member_v2 ?? "",
    vault_v2: input.vault_v2 ?? "",
    lamports: input.lamports,
    confirmationStatus: null as string | null,
    feeLamports: null as number | null,
    error: null as string | null,
  };

  try {
    // Wallet must be connected for SEND
    const w = input.wallet ?? (typeof window !== "undefined" ? (window as any).solana : null);
    if (!w) throw new Error("Wallet not available. Provide `wallet` or ensure window.solana is present.");
    if (typeof w.connect === "function" && !w.publicKey) await w.connect();
    if (!w.publicKey) throw new Error("Wallet not connected.");

    const programIdPk = new PublicKey(rt.programId);
    const poolPk = new PublicKey(asTrimmed(input.pool_v2));
    const walletPk = new PublicKey(w.publicKey.toString());

    // Preflight: pool exists on this cluster
    await preflightPoolExists(connection, poolPk);

    // Derive PDAs (contract-governed)
    const derivedMember = deriveMemberV2Pda(programIdPk, poolPk, walletPk);
    const derivedVault = deriveVaultV2Pda(programIdPk, poolPk);

    // Validate overrides if provided; otherwise use derived.
    const memberOverride = asTrimmed(input.member_v2);
    const vaultOverride = asTrimmed(input.vault_v2);

    const memberPk = memberOverride ? new PublicKey(memberOverride) : derivedMember;
    const vaultPk = vaultOverride ? new PublicKey(vaultOverride) : derivedVault;

    if (memberOverride && !memberPk.equals(derivedMember)) {
      throw new Error(
        `member_v2 mismatch.\n` +
          `- provided: ${memberPk.toBase58()}\n` +
          `- derived:  ${derivedMember.toBase58()}\n` +
          `Fix the pasted PDA or omit member_v2 so it is derived.`
      );
    }

    if (vaultOverride && !vaultPk.equals(derivedVault)) {
      throw new Error(
        `vault_v2 mismatch.\n` +
          `- provided: ${vaultPk.toBase58()}\n` +
          `- derived:  ${derivedVault.toBase58()}\n` +
          `Fix the pasted PDA or omit vault_v2 so it is derived.`
      );
    }

    // Keep receipt fields consistent (store effective PDAs)
    baseReceipt.member_v2 = memberPk.toBase58();
    baseReceipt.vault_v2 = vaultPk.toBase58();

    // 1) SEND (delegates signing + sending to sendContributeV2)
    const sent = await sendContributeV2({
      programId: rt.programId,
      endpoint: rt.endpoint,
      pool_v2: poolPk.toBase58(),

      // Overrides are allowed here, but we've already validated them.
      member_v2: memberPk.toBase58(),
      vault_v2: vaultPk.toBase58(),

      lamports: input.lamports,
      commitment,
      wallet: w,

      // optional passthrough (ignored if sendContributeV2 doesn't accept it)
      ...(typeof input.feeBufferLamports === "number" ? { feeBufferLamports: input.feeBufferLamports } : {}),
    } as any);

    const signature = sent.signature;
    console.log("=== contribute_v2 SEND submitted ===");
    console.log("signature:", signature);

    // 2) VERIFY (poll)
    const verified = await waitForSignature(connection, signature, {
      commitment,
      timeoutMs: input.verifyTimeoutMs ?? 60_000,
      pollMs: 1_500,
    });

    // 3) DETAILS (best-effort)
    const details = await fetchTxDetails(connection, signature, commitment);

    // 4) RECEIPT
    saveReceipt({
      ...baseReceipt,
      signature,
      confirmationStatus: verified.confirmationStatus ?? null,
      feeLamports: details.feeLamports ?? null,
      error: verified.err ? JSON.stringify(verified.err) : null,
    });

    if (verified.err) {
      throw new Error(`Transaction landed with error: ${JSON.stringify(verified.err)}`);
    }

    console.log("=== contribute_v2 VERIFIED ===");
    console.log("confirmationStatus:", verified.confirmationStatus);
    if (details.feeLamports != null) console.log("feeLamports:", details.feeLamports);

    return {
      signature,
      verified,
      details,

      // Useful for UI display / operator clarity
      effective: {
        endpoint: rt.endpoint,
        programId: rt.programId,
        wallet: walletPk.toBase58(),
        pool_v2: poolPk.toBase58(),
        member_v2: memberPk.toBase58(),
        vault_v2: vaultPk.toBase58(),
        derived: {
          member_v2: derivedMember.toBase58(),
          vault_v2: derivedVault.toBase58(),
        },
      },
    };
  } catch (e: any) {
    const msg = e?.message ? String(e.message) : String(e);

    saveReceipt({
      ...baseReceipt,
      error: msg,
    });

    throw e;
  }
}
